import { useState, useCallback } from "react";
import { doc, getDocs, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { requireTenantBandContext, tenantMembersCollectionRef } from "../tenantContext";

export function useMemberManagement({
  tenantContext,
  userRole,
  user,
  activeBandName,
  requestConfirm,
  dismissConfirm,
  patchTenant,
  showToast,
}) {
  const [members, setMembers] = useState([]);
  const [isMembersLoading, setIsMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");
  const [editingMemberUid, setEditingMemberUid] = useState(null);
  const [editingMemberName, setEditingMemberName] = useState("");
  const [editingMemberRole, setEditingMemberRole] = useState("member");
  const [isSavingMember, setIsSavingMember] = useState(false);
  const [removingUid, setRemovingUid] = useState(null);
  const [isTransferringOwnership, setIsTransferringOwnership] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || !tenantContext?.bandId) return;
    setIsMembersLoading(true);
    setMembersError("");
    try {
      const membersSnap = await getDocs(tenantMembersCollectionRef(tenantContext));
      const rows = membersSnap.docs.map((d) => {
        const { role, joinedAt, displayName } = d.data();
        return { uid: d.id, role, joinedAt, displayName: displayName || "Unknown" };
      });
      setMembers(rows);
    } catch (err) {
      console.error("Error fetching members:", err);
      setMembersError("Could not load members.");
    } finally {
      setIsMembersLoading(false);
    }
  }, [user, tenantContext]);

  const startEdit = useCallback((m) => {
    setEditingMemberUid(m.uid);
    setEditingMemberName(m.displayName || "");
    setEditingMemberRole(m.role || "member");
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingMemberUid(null);
  }, []);

  const handleSaveMember = useCallback(async (memberUid) => {
    const name = editingMemberName.trim();
    if (!name) return;
    setIsSavingMember(true);
    try {
      requireTenantBandContext(tenantContext);
      if (userRole === "admin") {
        const memberRef = doc(tenantMembersCollectionRef(tenantContext), memberUid);
        await updateDoc(memberRef, { displayName: name, role: editingMemberRole });
      } else {
        const userRef = doc(db, "users", memberUid);
        await updateDoc(userRef, { displayName: name });
      }
      setMembers((prev) =>
        prev.map((m) =>
          m.uid === memberUid
            ? { ...m, displayName: name, role: userRole === "admin" ? editingMemberRole : m.role }
            : m
        )
      );
      setEditingMemberUid(null);
    } catch (err) {
      console.error("Error saving member:", err);
      showToast("Couldn't save changes — please try again.");
    } finally {
      setIsSavingMember(false);
    }
  }, [editingMemberName, editingMemberRole, tenantContext, userRole, showToast]);

  const handleRemoveMember = useCallback((m) => {
    requestConfirm({
      title: "Remove member",
      message: `${m.displayName || "This member"} will lose access to ${activeBandName} immediately. You can re-invite them later.`,
      confirmLabel: "Remove member",
      danger: true,
      onConfirm: async () => {
        dismissConfirm();
        setRemovingUid(m.uid);
        try {
          const removeBandMember = httpsCallable(functions, "removeBandMember");
          await removeBandMember({ bandId: tenantContext.bandId, uid: m.uid });
          setMembers((prev) => prev.filter((x) => x.uid !== m.uid));
          showToast(`Removed ${m.displayName || "member"} from the band.`);
        } catch (err) {
          console.error("Error removing member:", err);
          showToast("Couldn't remove member — please try again.");
        } finally {
          setRemovingUid(null);
        }
      },
    });
  }, [activeBandName, tenantContext, requestConfirm, dismissConfirm, showToast]);

  const handleTransferOwnership = useCallback((member) => {
    requestConfirm({
      title: `Transfer ownership to ${member.displayName}?`,
      message:
        "They will become the band owner. You will remain an admin but will no longer be able to delete the band or transfer ownership.",
      confirmLabel: "Transfer ownership",
      danger: false,
      onConfirm: async () => {
        setIsTransferringOwnership(true);
        try {
          const transferOwnership = httpsCallable(functions, "transferOwnership");
          await transferOwnership({ bandId: tenantContext.bandId, newOwnerUid: member.uid });
          patchTenant({ ownerId: member.uid });
          dismissConfirm();
          showToast(`Ownership transferred to ${member.displayName}`);
        } catch (err) {
          console.error("Error transferring ownership:", err);
          showToast("Couldn't transfer ownership — please try again.");
          dismissConfirm();
        } finally {
          setIsTransferringOwnership(false);
        }
      },
    });
  }, [tenantContext, patchTenant, requestConfirm, dismissConfirm, showToast]);

  return {
    members,
    isMembersLoading,
    membersError,
    refresh,
    editingMemberUid,
    editingMemberName,
    editingMemberRole,
    setEditingMemberName,
    setEditingMemberRole,
    startEdit,
    cancelEdit,
    isSavingMember,
    removingUid,
    isTransferringOwnership,
    handleSaveMember,
    handleRemoveMember,
    handleTransferOwnership,
  };
}
